
import React, {useState} from 'react';
import styles from './_j-d-power-sport-button.module.scss';
import {Button} from "@mui/material";
import {IApplication} from "../../Interfaces/Application/IApplication";
import {getJdPowersportModels} from "../../Data/JDPowerData";
import {IJDPowersportModel} from "../../Interfaces/JDPower/IJDPowersportModel";
import {useDispatch, useSelector} from "react-redux";
import {ApplicationDispatch, SET_LOADING, SET_SERVER_MESSAGE} from "../../store/application/types";
import produce from "immer";
import {saveApp, updateEditingApp} from "../../store/application/actions";
import {useBusinessRulesEngine} from "../../Data/BusinessRulesData";
import {validateApp} from "../../utils/appUtils";
import {showValidationDialog} from "../../utils/ValidationDialog";
import {IConfiguration} from "../../Data/ConfigurationData";
import JDPowersportDialog from "../Dialogs/JDPowersportDialog/JDPowersportDialog";
import {useDialog} from "../../providers/DialogProvider";
import {IEmployee} from "../../Interfaces/Admin/IEmployee";
import { AppState } from '../../store/rootReducer';
import ErrorDialog from "../Dialogs/ErrorDialog";
import ConfirmationDialog from "../Dialogs/ConfirmationDialog";
import { Constants } from '../../utils/constants';
import {TradeInVehicle, Vehicle, VehicleJdPowersport} from "@amsanalytics/react-shared-library";

export interface JDPowerSportButtonProps {
  currentApp: IApplication;
  configuration: IConfiguration;
  buttonText: 'Unlock' | 'Decode';
  updateVinErrorMessage: (numberOfModels: number | undefined, initialLoad: boolean) => void;
  updateVinIsLocked: (vinIsLocked: boolean) => void;
  isTradeVehicle: boolean;
  tradeInSlot?: 1 | 2;
}

export default function JDPowersportButton(props:  JDPowerSportButtonProps) {
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const [openDialog, closeDialog] = useDialog();
  const [availableModels, setAvailableModels] = useState<IJDPowersportModel[] | undefined>([]);
  const tradeInSlot = props.tradeInSlot ?? 1;
  const tradeInVehicle = props.isTradeVehicle
    ? (tradeInSlot === 2 ? props.currentApp.tradeInVehicle2 : props.currentApp.tradeInVehicle)
    : undefined;
  const vehicle = props.isTradeVehicle ? tradeInVehicle :  props.currentApp.vehicle!;
  const vehicleJdPowersport = props.isTradeVehicle ? tradeInVehicle?.vehicleJdPowersport :  vehicle?.vehicleJdPowersport;
  const activeUser: IEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const defaultSetJdPowersportValueType = vehicleJdPowersport ? vehicleJdPowersport.valueType : props.configuration.defaultJdPowersportValueType ?? "AverageRetail";
  const [currentlyDecodingVin, setCurrentlyDecodingVin] = useState(false);

  const getValueForValueType = (type: string, model: IJDPowersportModel | undefined): number => {
    switch (type) {
      case "MSRP":
        return Number(model?.msrp ?? 0);
      case "LowRetail":
        return Number(model?.lowRetail ?? 0);
      case "AverageRetail":
        return Number(model?.averageRetail ?? 0);
      case "LowTrade":
        return Number(model?.lowTrade ?? 0);
      case "HighTrade":
        return Number(model?.highTrade ?? 0);
      case "AverageWholesale":
        return Number(model?.averageWholesale ?? 0);
      default:
        return 0;
    }
  }
  
  const getModels = async () => {
    const modelsInfo = props.isTradeVehicle ?
      await getJdPowersportModels(dispatch, props.currentApp.id, tradeInVehicle?.id ?? '', tradeInVehicle!.vin) :
      await getJdPowersportModels(dispatch, props.currentApp.id, props.currentApp.vehicleId ?? '', props.currentApp.vehicle!.vin);    
    
    props.updateVinErrorMessage(modelsInfo?.models?.length, false);


    if(modelsInfo?.models)
      modelsInfo.models.forEach(m => {
      m.selected = false;
      m.modelDescription = `${m.year} ${m.model} ${m.modelType}`;
    });
    setAvailableModels(modelsInfo?.models);
        

    if (modelsInfo?.models && modelsInfo?.models?.length === 1) {
      await handleSave(modelsInfo.models[0], defaultSetJdPowersportValueType, modelsInfo.vehicleBaseId);
    } else if (modelsInfo?.models && modelsInfo.models.length > 1) {      
      openDialog({
        children: (
          <JDPowersportDialog
            {...props}
            closeDialog={closeDialog}
            getValueForValueType={getValueForValueType}
            availableModels={modelsInfo!.models!}
            handleSave={ (modelToSave: IJDPowersportModel, jdPowersportValueTypeToSave: string | undefined) => handleSave(modelToSave, jdPowersportValueTypeToSave, modelsInfo.vehicleBaseId)}
          />
        )
      })
    }
  }
  
  const handleClick = async () => {
    if (props.buttonText === 'Unlock') {
      openDialog({
        children: (
          <ConfirmationDialog
            closeDialog={closeDialog} //cancel
            prompt="This will clear the motorcycle's info."
            title="Unlock the VIN?"
            accept={() => {
              props.updateVinIsLocked(false);
              closeDialog();
            }}
          />
        )
      })
    } else {
      setCurrentlyDecodingVin(true);
      dispatch({ type: SET_LOADING, payload: true });
      await getModels();
      setCurrentlyDecodingVin(false);
      dispatch({ type: SET_LOADING, payload: false });
    }
  }

  const handleSaveAppFromJdPowersport = async (bypassAppRequirements?: boolean) => {
    const errors: string[] = validateApp(props.currentApp!);

    if (errors.length === 0) {
      const result = businessRulesEngine({
        action: "Save",
      });
      if (result?.errors?.length && !bypassAppRequirements) {
        showValidationDialog({
          errors: result.errors,
          callback: () => {}
        });
      } else {
        await dispatch(saveApp(businessRulesEngine, false));
      }
    }
  }

  const handleSave = async (modelToSave: IJDPowersportModel, jdPowersportValueTypeToSave: string | undefined, vehicleId?: string) => {
    const vehicleJdPowersportToSave: VehicleJdPowersport = {
      id: vehicleJdPowersport?.id,
      vehicleBaseId: props.isTradeVehicle ?
        tradeInVehicle?.id === undefined
        ? Constants.emptyGuid
        : tradeInVehicle?.id
         : props.currentApp.vehicleId ?? Constants.emptyGuid ,
      make: modelToSave.make,
      makeId: modelToSave.makeID,
      year: modelToSave.year,
      model: modelToSave.model,
      modelId: modelToSave.modelID,
      modelType: modelToSave.modelType,
      modelNo: modelToSave.modelNo,
      msrp: Number(modelToSave.msrp),
      lowRetail: Number(modelToSave.lowRetail),
      averageRetail: Number(modelToSave.averageRetail),
      lowTrade: Number(modelToSave.lowTrade),
      highTrade: Number(modelToSave.highTrade),
      averageWholesale: Number(modelToSave.averageWholesale),
      transmission: modelToSave.transmission,
      weight: Number(modelToSave.weight),
      engineCc: Number(modelToSave.engineCC),
      stroke: Number(modelToSave.stroke),
      cylinders: Number(modelToSave.cylinders),
      valueType: jdPowersportValueTypeToSave ?? defaultSetJdPowersportValueType,
      selectedValue: getValueForValueType(jdPowersportValueTypeToSave ?? defaultSetJdPowersportValueType, modelToSave),
      pulledById: activeUser.id,
      pulledDate: new Date(),
      numberOfModelsForVin: availableModels?.length ?? 0,
      vinUsed: props.isTradeVehicle ? tradeInVehicle!.vin.replace(/\W/g,'') : props.currentApp.vehicle!.vin.replace(/\W/g,'')
    };

    const updatedApp = await produce(props.currentApp, appToUpdate => {
      if( !props.isTradeVehicle ){
        if (appToUpdate.vehicle) {
          if(vehicleId && (!appToUpdate.vehicle.id || appToUpdate.vehicle.id === Constants.emptyGuid)) {
            appToUpdate.vehicle.id = vehicleId;
          }      
          appToUpdate.vehicleId = appToUpdate.vehicle.id;  
          appToUpdate.vehicle.model = vehicleJdPowersportToSave.model;
          appToUpdate.vehicle.make = vehicleJdPowersportToSave.make;
          appToUpdate.vehicle.year = vehicleJdPowersportToSave.year;
          appToUpdate.vehicle.engineSize = vehicleJdPowersportToSave.engineCc;
          appToUpdate.vehicle.nadaValue = vehicleJdPowersportToSave.selectedValue;
          appToUpdate.vehicle.vehicleJdPowersport = {...vehicleJdPowersportToSave};        
        } else {
          appToUpdate.vehicle = {
            id: vehicleId,
            model: vehicleJdPowersportToSave.model,
            make: vehicleJdPowersportToSave.make,
            year: vehicleJdPowersportToSave.year,
            engineSize: vehicleJdPowersportToSave.engineCc,
            nadaValue: vehicleJdPowersportToSave.selectedValue,
            vehicleJdPowersport: {...vehicleJdPowersportToSave},          
            downPayment: 0
        } as Vehicle
        }
        appToUpdate.vehicleId = props.currentApp.vehicleId ?? vehicleId;
    } else { // TradeInVehicle
      const tradeKey = tradeInSlot === 2 ? 'tradeInVehicle2' : 'tradeInVehicle';
      if (appToUpdate[tradeKey]) {
        if(vehicleId && (!appToUpdate[tradeKey]!.id || appToUpdate[tradeKey]!.id === Constants.emptyGuid)) {
          appToUpdate[tradeKey]!.id = vehicleId;
        }
        appToUpdate[tradeKey]!.model = vehicleJdPowersportToSave.model;
        appToUpdate[tradeKey]!.make = vehicleJdPowersportToSave.make;
        appToUpdate[tradeKey]!.year = vehicleJdPowersportToSave.year;
        appToUpdate[tradeKey]!.tradeValue = vehicleJdPowersportToSave.selectedValue;
        appToUpdate[tradeKey]!.vehicleJdPowersport = {...vehicleJdPowersportToSave};
      } else {
        appToUpdate[tradeKey] = {
          id: vehicleId,
          model: vehicleJdPowersportToSave.model,
          make: vehicleJdPowersportToSave.make,
          year: vehicleJdPowersportToSave.year,
          tradeValue: vehicleJdPowersportToSave.selectedValue,
          vehicleJdPowersport: {...vehicleJdPowersportToSave}
       } as TradeInVehicle
      }
    }
    });
    await dispatch(updateEditingApp(updatedApp!, businessRulesEngine));

    // Locks the VIN if the vehicleJdPowersport is set
    props.updateVinIsLocked(true);
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: {isError: false, message: "JD Powersport value pulled successfully"},
    });
  }
  
  return (
    <div className={styles.jDPowerSportButtonContainer}>
      <Button
        onClick={ async (e) => {
          const trimmedVin = vehicle?.vin?.trim();
          
          if (props.buttonText === 'Decode' && trimmedVin?.length !== 17) {
            openDialog({
              children: (
                <ErrorDialog
                  title={`Vehicle Errors`}
                  errors={["The VIN must be 17 characters"]}
                  closeDialog={closeDialog}
                />
              )
            });
          } else {
            await handleClick();
          }
        }}
        variant="contained"
        color="inherit"
        disabled={currentlyDecodingVin}
      >
        {props.buttonText}
      </Button>
    </div>
  );
}
